
import { defineComponent } from 'vue';
import Form from '@/shared/mixins/Form';
import { mapGetters, mapState } from 'vuex';
import { GlobalState } from '@/shared/models/GlobalState';
import ResizableImage from '@/shared/components/ResizableImage.vue';
import MapPositionReadonly from '@/shared/components/MapPositionReadonly.vue';
import LoginButton from '@/account/components/LoginButton.vue';
import AdDetailGallery from '@/ad/components/AdDetailGallery.vue';
import AdInterestedForm from '@/ad/components/AdInterestedForm.vue';
import ReadMore from '@/shared/components/ReadMore.vue';
import AdShare from '@/ad/components/AdShare.vue';
import AdDetailHeader from '@/ad/components/AdDetailHeader.vue';
import AdListSimilar from '@/ad/components/AdListSimilar.vue';
import FixedElement from '@/shared/components/FixedElement.vue';
import { Point } from '@/shared/models/Point';
import { adService } from './AdService';
import AdComplaint from '@/ad/components/AdComplaint.vue';
import AdListItemLike from '@/ad/components/AdListItemLike.vue';
import AdListItemFavorite from '@/ad/components/AdListItemFavorite.vue';
import AdListItemCompare from '@/ad/components/AdListItemCompare.vue';
import AdImage from '@/ad/components/AdImage.vue';
import Modal from '@/shared/components/Modal.vue';
import { googleTagManagerService } from '@/shared/services/GoogleTagManagerService';
import AdLabels from '@/ad/components/AdLabels.vue';
import Banner from '../shared/components/Banner.vue';
import { config } from '@/shared/constants';
import { urlJoin } from '@/shared/helpers/urlJoin';
import AdDeveloperProjectList from '@/ad/components/AdDeveloperProjectList.vue';

export default defineComponent({
  name: 'AdDetail',
  mixins: [Form],
  components: {
    ResizableImage,
    MapPositionReadonly,
    LoginButton,
    AdDetailGallery,
    AdInterestedForm,
    ReadMore,
    AdShare,
    FixedElement,
    AdDetailHeader,
    AdListSimilar,
    AdComplaint,
    AdListItemLike,
    AdListItemFavorite,
    AdListItemCompare,
    AdImage,
    Modal,
    AdLabels,
    Banner,
    AdDeveloperProjectList,
  },
  props: {
    adFrontId: {
      type: String,
      required: true,
    },
    isPreview: {
      type: Boolean,
      default: false,
    },
  },
  // metaInfo() {
  //   return {
  //     title: (this as any).ad ? (this as any).ad.title : '',
  //     meta: [
  //       { name: 'description', content: (this as any).metaDescription },
  //       { property: 'og:title', content: (this as any).ad ? (this as any).ad.title : '' },
  //       { property: 'og:description', content: (this as any).metaDescription },
  //       { property: 'og:type', content: 'product' },
  //       //{ property: 'og:url', content: window.location.origin + this.$route.fullPath },
  //       { property: 'og:image', content: (this as any).defaultImage },
  //       { property: 'og:image:width', content: (this as any).ogImageWidth },
  //       { property: 'og:image:height', content: (this as any).ogImageHeight },
  //     ],
  //   };
  // },
  data() {
    return {
      isEstateAgencyMode: config.IS_ESTATE_AGENCY_MODE,
      ad: null as null | any,
      position: null as null | Point,
      isHeaderFixed: false,
      ogImageWidth: 1200,
      ogImageHeight: 630,
      showStreetView: false,
      showAddComplaintModal: false,
      showVirtualTourModal: false,
      showVideoModal: false,
      showWatchDog: false,
      webDomain: null as null | string,
    };
  },

  async created() {
    this.loadData();
  },

  computed: {
    ...mapState({
      user: (state) => (state as GlobalState).user,
    }),
    ...mapGetters(['isLoggedIn', 'isMobile']),
    isHouse(): boolean {
      if (this.ad) {
        return this.ad.estateFrontSubType > 200 && this.ad.estateFrontSubType < 300;
      }
      return false;
    },
    isEstateAgency(): boolean {
      return this.ad.estateAgencyId;
    },
    isDeveloperProject(): boolean {
      return this.ad.estateType === 500;
    },
    isDeveloperProjectEstate(): boolean {
      return this.ad.parentAdId && this.ad.parentAdId > 0;
    },
    address(): string {
      let address = '';
      address += this.formatAddressPart(address, '', this.ad.street);
      address += this.formatAddressPart(address, ' ', this.ad.streetNumber);
      address += this.formatAddressPart(address, ', ', this.ad.city);
      return address;
    },
    fixedClass(): null | string {
      return this.isMobile ? '' : 'fixed';
    },
    firstImage(): null | string {
      if (!this.ad || !this.ad.images || this.ad.images.length === 0) return '';

      return this.ad.images[0];
    },
    defaultImage(): null | string {
      if (!this.ad) return '';

      const image = this.ad.images && this.ad.images.length > 0 ? this.ad.images[0] : null;
      return adService.getImageOrPlaceHolder(this.ad.adGuid, this.ad.estateType, this.ad.estateSubType, image, {
        width: this.ogImageWidth,
        height: this.ogImageHeight,
        watermark: true,
      });
    },
    fullUrl() {
      return urlJoin(window.location.origin, config.FRONT_RELATIVE_APP_URL, this.$route.fullPath);
    },
    // metaDescription(): null | string {
    //   if (!this.ad) return '';

    //   let priceText = this.ad.price + ' ' + this.ad.currency;

    //   if (this.ad.adType === 2) {
    //     priceText += ' / mesiac';
    //   }

    //   return this.ad.description + ' ' + priceText;
    // },
    classByAdType(): string {
      switch (this.ad.adType) {
        case 1:
          return 'is-sale';
        case 2:
          return 'is-rent';
        case 3:
          return 'is-roommates';
      }
      return '';
    },
    whatsAppUrl(): string {
      if (this.isMobile) {
        return `https://api.whatsapp.com/send?phone=${this.ad.phoneNumber}`;
      } else {
        return `https://web.whatsapp.com/send?phone=${this.ad.phoneNumber}`;
      }
    },
  },
  watch: {
    isLoggedIn: function (newVal, oldVal) {
      if (newVal && newVal != oldVal) {
        this.loadData();
      }
    },
  },
  methods: {
    onPhone() {
      googleTagManagerService.send({
        event: 'zavolat_click',
      });
    },
    onWhatsApp() {
      googleTagManagerService.send({
        event: 'whatsapp_click',
      });
    },
    async loadData() {
      this.isLoading = true;
      try {
        const params = {} as any;
        if (this.isPreview) {
          params.isPreview = true;
        }
        const result = await adService.getDetail(this.adFrontId, params);
        this.ad = result.data;
        try {
          if (this.ad.web) {
            const url = new URL(this.ad.web);
            this.webDomain = url.hostname;
          }
        } catch (err) {
          console.error(err);
        }
        this.position = { lat: this.ad.latitude, lng: this.ad.longitude };
        this.isLoading = false;
      } catch (error) {
        if (error && error.response && error.response.status === 404) {
          this.$router.push({ name: '404' });
        } else {
          console.error(error);
        }
      }
    },
    webClick() {
      if (!this.isLoggedIn && this.$refs.loginButton) {
        //(this.$refs.loginButton as any).showModal();
      }
    },
    updateFixedStatus(isFixed: boolean) {
      this.isHeaderFixed = isFixed && !this.isMobile;
    },
    formatAddressPart(address: string, concatString: string, part: string) {
      if (!address) {
        concatString = '';
      }
      return part ? concatString + part : '';
    },
    scrollToInterestedForm() {
      const el = document.getElementById('contact-form-section');

      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    onStreetView() {
      this.scrollToMap();

      if (this.$refs.map && (this.$refs.map as any).$refs.mapControls) {
        (this.$refs.map as any).$refs.mapControls.onStreetView();
      }
    },
    scrollToMap() {
      const el = document.getElementById('estate-detail-map');

      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    onVirtualTour() {
      this.showVirtualTourModal = true;
    },
    onVideo() {
      //this.showVideoModal = true;
      window.open(this.ad.videoLink);
    },
    async translate(showTranslation) {
      const params = {} as any;
      params.languageId = 'en';
      if (this.isPreview) {
        params.isPreview = true;
      }
      const result = await adService.getDescription(this.adFrontId, params);
      this.ad.descriptionEn = result.data.description;
      showTranslation();
    },
    onNavigate() {
      this.scrollToMap();
      //const navigationUrl = (this.$refs.map as any).$refs.mapControls.navigationUrl;
      //window.open(navigationUrl);
    },
    print() {
      googleTagManagerService.send({
        event: 'tlacit_click',
      });
      window.print();
    },
    onMapInitialized() {
      if(!window.google || !window.google.maps) {
        return;
      }
      const sv = new window.google.maps.StreetViewService();
      sv.getPanorama({ location: this.position, radius: 50 }, (result, status) => {
        this.showStreetView = status === window.google.maps.StreetViewStatus.OK;
      });
    },
    async favorite() {
      if (this.ad.isFavorite) {
        const result = await adService.removeFavorite(this.ad.adId);
        if (result.status === 200) {
          this.ad.isFavorite = false;
        }
      } else {
        const result = await adService.addFavorite(this.ad.adId);
        if (result.status === 200) {
          this.ad.isFavorite = true;
        }
      }
    },
    async compare() {
      const result = await adService.addCompare(this.ad.adId);
      if (result.status === 200) {
        this.ad.isCompare = true;
        if (this.$router) {
          const routeData = this.$router.resolve({ name: 'AdCompare', query: { adCompareId: result.data.adCompareId } });
          window.open(routeData.href, '_blank');
        }
      }
    },
    async watchDog() {
      if (!this.isLoggedIn) {
        return;
      }
      googleTagManagerService.send({
        event: 'watchdog_click',
      });
      if (this.ad.isWatch) {
        const result = await adService.unwatch(this.ad.adId);
        if (result.status === 200) {
          this.ad.isWatch = false;
        }
      } else {
        const result = await adService.watch(this.ad.adId);
        if (result.status === 200) {
          this.ad.isWatch = true;
        }
      }
    },
  },
});
